import React, { FC, useState } from 'react';
import { Image, Button, Tooltip } from 'antd';
import { NavLink } from 'react-router-dom';
import { SidebarItem } from 'common/components';
import { ERoute } from 'common/const';
import { getSidebarHeaderSubtitle, getSidebarItemClassName, getSidebarItems } from 'common/helpers';
import { AuthHooks, UserHooks } from 'common/hooks';
import { ISidebarItem } from 'common/models';
import { PREVIOUS_PATH_KEY } from 'common/config';
import { ArrowRightShort, Collapse, Expand } from 'common/svg';
import sidebar from 'app/assets/images/redesign/sidebar.svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { ESettingsMenuTab, ESettingsTab } from 'entities/Settings/Settings.enum';
import { selectIsSidebarOpen, toggleIsSidebarOpen } from 'entities/UI/UI.slice';
import { deleteAuth, logoutAsUser, selectXBasketId, selectXUserId } from 'entities/Auth/Auth.slice';
import { selectCurrentUser, selectUserStats } from 'entities/User/User.slice';

export const Sidebar: FC = () => {
  const dispatch = useAppDispatch();
  // auth
  const xUserId = useAppSelector(selectXUserId);
  const xBasketId = useAppSelector(selectXBasketId);
  // ui
  const isSidebarOpen = useAppSelector(selectIsSidebarOpen);
  // user
  const currentUser = useAppSelector(selectCurrentUser);
  const userStats = useAppSelector(selectUserStats);

  const [headerItemHovered, setHeaderItemHovered] = useState<boolean>(false);
  const [bodyHovered, setBodyHovered] = useState<boolean>(false);
  const [hoveredItemKey, setHoveredItemKey] = useState<string>('');
  const { isSeller } = AuthHooks.useIsSeller();
  const { isSuperAdmin, isAccountAdmin, isSellerManager } = UserHooks.useCurrentUserRole();

  const accountName = currentUser?.account?.name;
  const headerSubtitle = getSidebarHeaderSubtitle(currentUser?.roles, isSeller);

  const onLogout = () => {
    xUserId ? dispatch(logoutAsUser()) : dispatch(deleteAuth());
  };

  const items = getSidebarItems(isSeller, xUserId, xBasketId, currentUser, userStats, onLogout);

  const onExpandClick = () => {
    dispatch(toggleIsSidebarOpen());
  };

  const toggleBodyHovered = () => setBodyHovered((prev) => !prev);

  const toggleHeaderItemHovered = () => setHeaderItemHovered((prev) => !prev);

  const getSettingsLink = () => {
    if (isSeller && isAccountAdmin) {
      return `${ERoute.Settings}/${ESettingsTab.Account}/${ESettingsMenuTab.UserList}`;
    }

    if (isSeller && isSellerManager) {
      return `${ERoute.Settings}/${ESettingsTab.Contract}`;
    }

    return `${ERoute.Settings}/${ESettingsTab.Account}/${ESettingsMenuTab.Contracts}`;
  };

  const renderItem = (item: ISidebarItem) => {
    return (
      <SidebarItem
        key={item.key}
        item={item}
        sidebarOpened={!isSidebarOpen}
        bodyHovered={bodyHovered}
        hoveredKey={hoveredItemKey}
        onHoveredKeyChange={setHoveredItemKey}
      />
    );
  };

  return (
    <div className="redesign sidebar__wrapper">
      <Button className="sidebar__expand-btn" icon={isSidebarOpen ? <Expand /> : <Collapse />} onClick={onExpandClick} />

      <div className={`sidebar ${isSidebarOpen ? 'sidebar__expanded' : 'sidebar__collapsed'}`}>
        <div className="sidebar__header">
          <NavLink
            to={getSettingsLink()}
            className={({ isActive }) => getSidebarItemClassName(isActive, 'sidebar-settings')}
            style={{ pointerEvents: !isSuperAdmin && (isAccountAdmin || isSellerManager) ? 'auto' : 'none' }}
            onClick={() => {
              if (!location.pathname.startsWith(ERoute.Settings)) {
                sessionStorage.setItem(PREVIOUS_PATH_KEY, `${location.pathname}${location.search}`);
              }
            }}
            onMouseEnter={toggleHeaderItemHovered}
            onMouseLeave={toggleHeaderItemHovered}
          >
            <Tooltip
              overlayClassName="sidebar__header-tooltip"
              open={!isSidebarOpen && headerItemHovered}
              title="Настройки аккаунта"
              placement="right"
            >
              <Image wrapperClassName="sidebar__header-image" src={sidebar} preview={false} />
            </Tooltip>

            <div className="sidebar-item__label">
              <div>
                <div className="mb-2 ellipsis-2 text-h4 color-black" title={accountName}>
                  {accountName}
                </div>

                <div className="ellipsis-2 text-tag color-dark-grey" title={headerSubtitle ? headerSubtitle : undefined}>
                  {headerSubtitle}
                </div>
              </div>

              {!isSuperAdmin && (isAccountAdmin || isSellerManager) && (
                <Button className="button-icon" icon={<ArrowRightShort className="icon-arrow-right-short-dark-grey" />} />
              )}
            </div>
          </NavLink>
        </div>

        <div className="sidebar__body" onMouseEnter={toggleBodyHovered} onMouseLeave={toggleBodyHovered}>
          <div className="sidebar__body-group orange-group">{items.body.orangeGroup.map(renderItem)}</div>

          <div className="sidebar__body-group dark-green-group">{items.body.darkGreenGroup.map(renderItem)}</div>

          <div className="sidebar__body-group bright-green-group">{items.body.brightGreenGroup.map(renderItem)}</div>
        </div>

        <div className="sidebar__footer">{items.footer.map(renderItem)}</div>
      </div>
    </div>
  );
};
